import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { DocumentIcon, FileOutlineIcon, JobsIcon, PreferencesIcon } from "../../assets/icons/interface/interface";
import Drawer from "../../components/Drawer/Drawer";
import { actions } from "../../handlers/enums";
import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useResumes, useSmartProfile, useUserAccount } from "../../services/create-resume/queries";
import { ResumeDataService } from "../../services/create-resume/resume-data";
import BulkFitCheckFlow from "../FitCheck/components/BulkFitCheckFlow";
import FitCheckBasketIcon from "../FitCheck/components/FitCheckBasketIcon";
import { Button } from "../JobsPage/components/Button";
import Loader from "../StaticPages/HomePage/components/Loader";
import ViewJob from "../StaticPages/HomePage/components/ViewJob";
import DocumentCenter from "./components/DocumentCenter";
import DSPItem from "./components/DSPItem";
import FloatingNav from "./components/FloatingNav";
import GmailConnectAccess from "./components/GmailConnectAccess";
import Header from "./components/Header";
import JobPreferencesModal from "./components/JobPreferencesModal";
import { Modal } from "./components/Modal";
import OutlookConnectAccess from "./components/OutlookConnectAccess";
import PreferencesUpsellCard from "./components/PreferencesUpsellCard";
import ProfileCard from "./components/ProfileCard";
import RecommendedJobs from "./components/RecommendedJobs";
import SavedJobs from "./components/SavedJobs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/Tabs";
import UploadResumeModal from "./components/UploadResumeModal";

function transformToRequestBody(currentPayload, personalInfo) {
	return {
		emailAddress: personalInfo.emailAddress,
		firstName: personalInfo.firstName,
		middleName: personalInfo.middleName,
		lastName: personalInfo.lastName,
		phoneNumber: personalInfo.phoneNumber,
		jobPreferences: {
			primaryResumeReferenceId: currentPayload.primaryResumeReferenceId,
			currentJobsearchStatus: currentPayload.currentJobsearchStatus,
			openForWorkTypes: currentPayload.openForWorkTypes,
			areasOfExpertise: currentPayload.areasOfExpertise,
			seniorityLevel: currentPayload.seniorityLevel,
			locationPreferenceType: currentPayload.locationPreferenceType,
			locationsPreferred: currentPayload.locationsPreferred,
			recentGraduate: currentPayload.recentGraduate,
			graduationDate: {
				month: currentPayload.graduationDate?.month,
				year: currentPayload.graduationDate?.year,
			},
			professionalSince: {
				month: currentPayload.professionalSince?.month,
				year: currentPayload.professionalSince?.year,
			},
			educationLevels: currentPayload.educationLevels,
			desiredTitle: currentPayload.desiredTitle,
			address: {
				city: currentPayload?.address?.city,
				country: "Tanzania",
			},
			skills: currentPayload.skills,
		},
	};
}

/**
 * Onboarding only captures 3 of the 15 preferences the backend scores, so a freshly signed-up user
 * lands at 20%. Keep nudging them until they've filled in most of it.
 */
const UPSELL_THRESHOLD_PCT = 60;

const navItems = [
	{
		id: "profile",
		label: "Preferences",
		icon: <PreferencesIcon className="w-5 h-5" />,
	},
	{
		id: "job-recommendations",
		label: "Job Picks",
		icon: <JobsIcon className="w-5 h-5" />,
	},
	{
		id: "documents",
		label: "Documents",
		icon: <DocumentIcon className="w-5 h-5" />,
	},
];

function Profile() {
	const showDSP = useFeatureFlag("dsp");

	const isLoggedIn = useIsLoggedIn();
	const dispatch = useDispatch();
	const resumeDataService = useMemo(() => new ResumeDataService(), []);

	const [isScrolled, setIsScrolled] = useState(false);
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [uploadResumeModal, setUploadResumeModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedDSPId, setSelectedDSPId] = useState(null);
	const [showFitCheckBasket, setShowFitCheckBasket] = useState(false);

	const [showPreferencesModal, setShowPreferencesModal] = useState(false);
	const [completenessPercentage] = useState(0);
	// The store has no RootState type yet, so the reducer map isn't inferable here.
	const dspProfileState = useSelector((state: any) => state.ProfileDSPReducer);

	const { data: resumeList, isLoading: isLoadingResumes } = useResumes();

	const [showDocument, setShowDocument] = useState(false);
	const [documentName, setDocumentName] = useState("");
	const [documentUrl, setDocumentUrl] = useState(null);
	const [loadingDocument, setLoadingDocument] = useState(true);

	useEffect(() => {
		if (isLoadingResumes) return;
	}, [resumeList, isLoadingResumes]);

	const [jobPreferences, setJobPreferences] = useState({});

	const { data: accountData, isLoading: isLoadingAccount, refetch } = useUserAccount();
	const { data: smartProfileData, isLoading: isLoadingSmartProfileData } = useSmartProfile();

	// A 0% account gets the full-size card in the job picks slot instead — see RecommendedJobs — so
	// don't nudge twice.
	const preferencesProgress = accountData?.job_preference_progress_status;
	const preferencesCompletenessPct = preferencesProgress?.completeness_pct ?? 0;
	const showPreferencesUpsell = preferencesCompletenessPct > 0 && preferencesCompletenessPct < UPSELL_THRESHOLD_PCT;

	useEffect(() => {
		if (isLoadingAccount) return;
		setJobPreferences(accountData?.jobPreferences);
		// Incomplete preferences are surfaced by the upsell banner above rather than by force-opening
		// this modal over the page.
	}, [accountData, isLoadingAccount]);

	const onChangeJobStatus = (status) => {
		setJobPreferences({
			...jobPreferences,
			currentJobsearchStatus: status,
		});
		resumeDataService.jobPreferences(
			transformToRequestBody(jobPreferences, {
				emailAddress: accountData?.email,
				firstName: accountData?.firstName,
				middleName: accountData?.middleName,
				lastName: accountData?.lastName,
				phoneNumber: accountData?.phoneNumber,
			}),
			(data) => {
				refetch();
				localStorage.setItem("jobPreferences", JSON.stringify(data?.data));
			},
			(error) => {
				const errorMessages = error.response.data.message;
				toast.error(errorMessages);
			},
		);
	};

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const payload = {
			fromDSP: false,
			actionPerformed: "",
		};

		if (dspProfileState.fromDSP && dspProfileState.actionPerformed === actions.CREATE) {
			setUploadResumeModal(true);
		}

		if (dspProfileState.fromDSP && dspProfileState.actionPerformed === actions.UPDATE) {
			const element = document.getElementById("documents");
			if (element) element.scrollIntoView({ behavior: "smooth" });
		}

		dispatch({ type: "RESET_DSP_STATE", payload });
	}, []);

	if (isLoadingResumes || isLoadingSmartProfileData) {
		return <div className="w-full h-screen bg-white flex items-center justify-center">Loading...</div>;
	}

	return (
		<>
			<Drawer
				isOpen={showDocument}
				onClose={() => {
					setDocumentUrl(null);
					setDocumentName("");
					setLoadingDocument(true);
					setShowDocument(false);
				}}
			>
				{showDocument && documentUrl && (
					<div>
						{loadingDocument && (
							<div className="h-[calc(88vh)] flex flex-col items-center justify-center">
								<Loader />
							</div>
						)}
						{!loadingDocument && (
							<div className="flex items-center py-[16px] gap-[8px]">
								<FileOutlineIcon className="w-[18px] h-[18px]" />
								<h6 className="text-[14px] font-[600]">{documentName}</h6>
								<Button
									variant="outline"
									className="ml-auto"
									onClick={() => {
										const link = document.createElement("a");
										link.href = documentUrl;
										link.download = documentName || "document";
										link.target = "_blank";
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
									}}
								>
									Download
								</Button>
							</div>
						)}
						<iframe
							src={documentUrl}
							className="w-full h-[calc(88vh)] rounded-lg border border-gray-100"
							title="Document Preview"
							onLoad={() => {
								setLoadingDocument(false);
							}}
						/>
					</div>
				)}
			</Drawer>
			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-sm" : ""}`}>
				<Header isLoggedIn={isLoggedIn} variant="applicant" onChangeJobStatus={onChangeJobStatus} />
			</div>

			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>
			<Modal
				isOpen={uploadResumeModal}
				onClose={() => {
					setUploadResumeModal(false);
				}}
				title="Get noticed. Get Hired – Upload your resume!"
				size="xl"
			>
				<UploadResumeModal onClose={() => setUploadResumeModal(false)} />
			</Modal>

			<div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<FloatingNav items={navItems} />

				{showPreferencesUpsell && (
					<PreferencesUpsellCard
						variant="inline"
						completenessPct={preferencesCompletenessPct}
						onOpenPreferences={() => setShowPreferencesModal(true)}
					/>
				)}

				{/* Profile Section */}
				<section id="profile" className="scroll-mt-24 space-y-4">
					<ProfileCard
						onEditPreferences={() => setShowPreferencesModal(true)}
						setUploadResumeModal={setUploadResumeModal}
						smartProfileData={smartProfileData}
					/>

					{showDSP && (
						<div className="bg-blue-50/50 rounded-lg p-6 border gap-4 border-blue-100 h-full flex flex-col md:flex-row items-center">
							<img
								alt="Complete your profile"
								loading="lazy"
								decoding="async"
								data-nimg="fill"
								className="object-contain h-full w-full left-0 top-0 right-0 bottom-0 max-w-[200px]"
								src="/images/job-applicant.png"
							/>
							<div className="flex flex-col md:flex-row items-center gap-8 md:gap-4">
								<div className="space-y-1">
									<h2 className="text-lg font-semibold tracking-normal text-gray-900 mt-1 mb-2">
										Send Application From Your Own Inbox
									</h2>
									<p className="text-gray-800 text-sm mb-8">
										Make your application stand out by sending them directly from your Gmail-not just a platform. With
										one, employers get a personal email from you, not Dproz. Replies go straight to your inbox.
									</p>
								</div>
								<GmailConnectAccess />
								<OutlookConnectAccess />
							</div>
						</div>
					)}
				</section>

				{/* Job Preferences Modal */}
				<JobPreferencesModal
					isOpen={showPreferencesModal}
					onClose={() => {
						setShowPreferencesModal(false);
						refetch();
					}}
					setJobPreferences={setJobPreferences}
					jobPreferences={jobPreferences}
					resumeList={resumeList}
					transformToRequestBody={transformToRequestBody}
				/>

				{/* Job Recommendations Section */}
				<section id="job-recommendations" className="scroll-mt-24 mt-12">
					<div className="mb-4">
						<h2 className="text-xl font-bold text-gray-800 font-plus-jakarta">Job picks for you</h2>
					</div>

					<Tabs
						defaultValue={accountData?.jobPreferences?.areasOfExpertise?.length > 0 ? "recommended" : "saved"}
						className="w-full"
					>
						<TabsList className="flex border-b-0">
							<TabsTrigger
								value="recommended"
								className="ignore-focus-behavior rounded-none text-sm text-gray-700 mr-6"
							>
								Recommended
							</TabsTrigger>
							<TabsTrigger value="saved" className="ignore-focus-behavior rounded-none text-sm text-gray-700">
								Saved Jobs
							</TabsTrigger>
						</TabsList>

						<TabsContent value="recommended">
							<RecommendedJobs
								setShowPreferencesModal={setShowPreferencesModal}
								setSelectedJob={setSelectedJob}
								setShowJobDetailsModal={setShowJobDetailsModal}
							/>
						</TabsContent>

						<TabsContent value="saved">
							<SavedJobs setSelectedJob={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
						</TabsContent>
					</Tabs>

					{completenessPercentage > 0 && (
						<div className="mt-8">
							<Link to="/jobs">
								<div className="w-full py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 text-sm font-inter text-center">
									View all jobs
								</div>
							</Link>
						</div>
					)}
				</section>

				{showDSP && Object.keys(smartProfileData).length > 0 && (
					<section className="mt-[32px]">
						<DSPItem
							dspList={smartProfileData ? smartProfileData.dsps : []}
							setDSPId={(value) => setSelectedDSPId(value)}
							smartProfile={smartProfileData}
						/>
					</section>
				)}

				{/* Documents Section */}
				{Object.keys(smartProfileData).length > 0 && (
					<section id="documents" className="scroll-mt-24">
						{/* smartProfile is not a prop — DocumentCenter calls useSmartProfile() itself. */}
						<DocumentCenter
							selectedDSPId={selectedDSPId}
							setShowDocument={setShowDocument}
							setDocumentName={setDocumentName}
							setDocumentUrl={setDocumentUrl}
							setLoadingDocument={setLoadingDocument}
						/>
					</section>
				)}
			</div>

			{/* FitCheck Basket Floating Icon */}
			<div className="fixed bottom-6 right-6 z-40">
				<FitCheckBasketIcon onClick={() => setShowFitCheckBasket(true)} />
			</div>

			{/* FitCheck Basket Drawer */}
			<BulkFitCheckFlow isOpen={showFitCheckBasket} onClose={() => setShowFitCheckBasket(false)} />
		</>
	);
}

export default Profile;
